<template>
  <div>
    <!-- 套餐选择 -->
    <h3 class="text-lg font-medium pl-4 mb-2 ">{{ $t("products.packages") }}</h3>

    <div class="bg-white rounded-lg p-4 mb-3 mx-4 w-[calc(100%-2rem)] hidden md:block">
      <div class="grid grid-cols-4 gap-3">
        <div
          v-for="(item, index) in firstLevelOptions"
          :key="index"
          :class="[
            'p-1 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 text-sm font-medium',
            currentFirstLevelIndex === index
              ? 'bg-orange-500 text-white border-2 border-orange-500'
              : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:border-gray-300',
          ]"
          @click.stop="handlePackageChange(index)">
          {{ isZh ? item.name : item.nameEn }}
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg p-3 mb-3 mx-4 w-[calc(100%-2rem)] md:hidden">
      <div v-for="(row, rowIndex) in firstLevelOptionsRows" :key="rowIndex" class="grid grid-cols-4 gap-2.5 mb-3 last:mb-0">
        <div
          v-for="(item, itemIndex) in row"
          :key="`${rowIndex}-${itemIndex}`"
          :style="{ gridColumn: `span ${item.colCount}` }"
          :class="[
            'py-1 px-0.5 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 text-sm font-medium',
            currentFirstLevelIndex === item.originalIndex
              ? 'bg-orange-500 text-white border-2 border-orange-500'
              : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:border-gray-300',
          ]"
          @click.stop="handlePackageChange(item.originalIndex)">
          {{ isZh ? item.name : item.nameEn }}
        </div>
      </div>
    </div>

    <!-- 天数选择 -->
    <h3 class="text-lg font-medium pl-4 mb-2">{{ $t("products.days") }}</h3>
    <div class="bg-white rounded-lg p-3 mb-3 mx-4 w-[calc(100%-2rem)]" v-if="secondLevelOptions.length > 0">
      <div class="grid grid-cols-5 gap-3">
        <div
          v-for="(item, index) in secondLevelOptions"
          :key="index"
          :class="[
            'p-1 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 text-sm font-medium',
            currentSecondLevelIndex === index
              ? 'bg-orange-500 text-white border-2 border-orange-500'
              : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:border-gray-300',
          ]"
          @click.stop="handleDaysChange(index)">
          {{ isZh ? item.name : item.nameEn }}
        </div>
      </div>
    </div>

    <!-- 数量选择 -->
    <h3 class="text-lg font-medium pl-4 mb-2">{{ $t("products.quantity") }}</h3>
    <div class="bg-white rounded-lg p-3 mb-3 mx-4 w-[calc(100%-2rem)]">
      <van-stepper v-model="quantity" min="1" :max="currentStock" class="border-0" />
    </div>

    <!-- 价格显示 -->
    <div class="bg-white rounded-lg p-3 mb-2 mx-4 w-[calc(100%-2rem)]">
      <h3 class="text-lg font-medium mb-2">{{ $t("products.price") }}</h3>
      <div class="text-[#ff7a00] line-through text-sm mb-1">RM {{ formatToYuan(currentMarketPrice) }}</div>
      <div class="text-[#ff7a00] text-2xl font-bold">RM {{ formatToYuan(currentPrice) }}</div>
    </div>
  </div>
</template>

<script>
import { Stepper } from "vant"
import { formatToYuan } from "@/utils/common"
export default {
  name: "ProductOptions",
  components: {
    [Stepper.name]: Stepper,
  },
  props: {
    skus: {
      type: Array,
      default: () => [],
    },
    isZh: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      packages: [
        { label: "50GB", value: "50GB" },
        { label: "30GB", value: "30GB" },
        { label: "20GB", value: "20GB" },
        { label: "10GB", value: "10GB" },
        { label: "每日3GB", value: "每日3GB" },
        { label: "每日2GB", value: "每日2GB" },
        { label: "每日1GB", value: "每日1GB" },
        { label: "无限流量", value: "无限流量" },
      ],
      dayOptions: [
        { label: "5天", value: 5 },
        { label: "7天", value: 7 },
        { label: "8天", value: 8 },
        { label: "10天", value: 10 },
        { label: "12天", value: 12 },
        { label: "15天", value: 15 },
        { label: "20天", value: 20 },
        { label: "30天", value: 30 },
        { label: "90天", value: 90 },
      ],
      currentFirstLevelIndex: 0, // 当前选中的一级选项索引
      currentSecondLevelIndex: 0, // 当前选中的二级选项索引
      quantity: 1, // 数量
      formatToYuan
    }
  },
  computed: {
    // 一级选项列表
    firstLevelOptions() {
      // console.log('this.skus :>> ', this.skus);
      return this.skus || []
    },
    // 计算每个选项应该占用的列数
    firstLevelOptionsWithCols() {
      return this.firstLevelOptions.map((item, originalIndex) => {
        const text = this.isZh ? item.name : item.nameEn
        const colCount = this.calculateColCount(text)
        return {
          ...item,
          colCount,
          originalIndex, // 保存原始索引用于判断选中状态
        }
      })
    },
    // 将选项分组到不同的行中
    firstLevelOptionsRows() {
      const rows = []
      let currentRow = []
      let currentColCount = 0

      this.firstLevelOptionsWithCols.forEach((item) => {
        // 如果当前行的列数加上这个项目的列数超过4，换到下一行
        if (currentColCount + item.colCount > 4 && currentRow.length > 0) {
          rows.push(currentRow)
          currentRow = []
          currentColCount = 0
        }

        currentRow.push(item)
        currentColCount += item.colCount

        // 如果当前行的列数正好是4，或者达到4列，换到下一行
        if (currentColCount >= 4) {
          rows.push(currentRow)
          currentRow = []
          currentColCount = 0
        }
      })

      // 添加最后一行
      if (currentRow.length > 0) {
        rows.push(currentRow)
      }

      return rows
    },
    // 当前选中一级选项对象
    currentFirstLevel() {
      if (this.currentFirstLevelIndex === null) return null
      const firstLevel = this.firstLevelOptions[this.currentFirstLevelIndex]
      // console.log("firstLevel :>> ", firstLevel)
      return firstLevel || null
    },
    // 当前一级下的二级选项列表
    secondLevelOptions() {
      return this.currentFirstLevel && this.currentFirstLevel.dayList ? this.currentFirstLevel.dayList : []
    },
    // 当前选中二级选项对象
    currentSecondLevel() {
      if (this.currentSecondLevelIndex === null) return null
      const secondLevel = this.secondLevelOptions[this.currentSecondLevelIndex]
      // console.log("secondLevel :>> ", secondLevel)
      return secondLevel || null
    },
    // 当前选中SKU的价格
    currentPrice() {
      if (this.currentSecondLevel) {
        return this.currentSecondLevel.price
      }
      if (this.currentFirstLevel && (!this.currentFirstLevel.dayList || this.currentFirstLevel.dayList.length === 0)) {
        return this.currentFirstLevel.price
      }
      return 0
    },
    // 当前选中SKU的市场价格
    currentMarketPrice() {
      if (this.currentSecondLevel) {
        return this.currentSecondLevel.marketPrice
      }
      if (this.currentFirstLevel && (!this.currentFirstLevel.dayList || this.currentFirstLevel.dayList.length === 0)) {
        return this.currentFirstLevel.marketPrice
      }
      return 0
    },
    // 当前选中SKU的库存
    currentStock() {
      if (this.currentSecondLevel) {
        return this.currentSecondLevel.stock
      }
      if (this.currentFirstLevel && (!this.currentFirstLevel.dayList || this.currentFirstLevel.dayList.length === 0)) {
        return this.currentFirstLevel.stock
      }
      return 0
    },
  },
  methods: {
    // 计算文本应该占用的列数
    calculateColCount(text) {
      // 判断是中文还是英文（简单判断：如果包含中文字符则为中文）
      const hasChinese = /[\u4e00-\u9fa5]/.test(text)

      if (hasChinese) {
        // 中文：计算字符长度，点号算0.5个字符
        let length = 0
        for (let char of text) {
          if (char === '.') {
            length += 0.5
          } else {
            length += 1
          }
        }
        // 中文：超过5个字符占2列，超过10个字符占3列
        if (length > 12) return 3
        if (length > 6) return 2
        return 1
      } else {
        const length = text.length
        // 英文：超过9个字符占2列，超过18个字符占3列
        if (length > 22) return 3
        if (length > 10) return 2
        return 1
      }
    },
    // 获取已选文本
    getSelectedText() {
      let text = ""

      if (this.currentFirstLevel) {
        text += this.currentFirstLevel.name + "  "
      }
      if (this.currentSecondLevel) {
        text += this.currentSecondLevel.name
      }
      return text
    },
    // 获取已选文本英文
    getSelectedTextEn() {
      let text = ""

      if (this.currentFirstLevel) {
        text += this.currentFirstLevel.nameEn + "  "
      }
      if (this.currentSecondLevel) {
        text += this.currentSecondLevel.nameEn
      }
      return text
    },
    selectPackage(value) {
      this.$emit("package-change", value)
    },
    selectDays(value) {
      this.$emit("days-change", value)
    },
    handlePackageChange(index) {
      this.currentFirstLevelIndex = index
      this.$emit("change")
    },
    handleDaysChange(index) {
      this.currentSecondLevelIndex = index
      this.$emit("change")
    },
  },
}
</script>

<style scoped></style>
